import Image from "next/image";
import { useRouter } from "next/router";
import { MdOutlineChat, MdOutlinePerson, MdOutlineTimer } from "react-icons/md";
import { useTranslation } from "next-i18next";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { events, track } from "@shared/tracking";
import { getCampaignIdAndUserId } from "@redux/sessionStorage/sessionItem";
import type { Activity } from "@components/fullScreenVideo/ActivitiesCard";

interface ActivityItemProps {
  activity: Activity;
  section: string;
  onClick?: () => void;
  isActivityExpanded?: boolean;
  isFullScreenVideo: boolean;
  isSingleActivity?: boolean;
  trackingId?: string;
}

export const ActivityItem = ({
  activity,
  section,
  onClick,
  isActivityExpanded = false,
  isFullScreenVideo = false,
  isSingleActivity = false,
  trackingId,
}: ActivityItemProps) => {
  const { t: translate } = useTranslation();

  const router = useRouter();
  const cityId = (router.query.id as string) ?? "";

  const getUrl = () => {
    const cmpId = getCampaignIdAndUserId();

    let url = activity?.url;
    if (url?.includes("viator.com")) {
      url = `${url}&campaign=${cmpId}`;
    }
    if (url?.includes("getyourguide.com") || url?.includes("civitatis.com")) {
      url = `${url}&cmp=${cmpId}`;
    }
    return url;
  };

  const price = {
    original: +activity.originalPrice,
    minimum: +activity.minimumPrice,
    currency: activity?.currency === "EUR" ? "€" : "$",
  };

  return (
    <a
      className={`w-full outline-none rounded-xl bg-[#121212] flex flex-col px-4 ${
        isSingleActivity ? "mb-1" : "border border-[#4B4B4B] mb-2"
      }`}
      href={getUrl()}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => {
        e.stopPropagation();
        track(events.CHECK_ACTIVITY, {
          activity_id: String(activity?.id),
          section,
          city_id: cityId,
        });
        onClick && onClick();
      }}
      id={"panel" + trackingId}
    >
      <h4
        className={`w-full py-1 font-bold text-white ${
          isSingleActivity ? "text-sm" : ""
        }`}
      >
        {activity.name}
      </h4>
      <div className="flex items-center justify-between w-full">
        {/* LEFT PART */}
        <div
          className={`flex items-center justify-center mr-1 ${
            isSingleActivity ? " w-1/4" : " w-1/3"
          }`}
        >
          <Image
            alt={"provider"}
            src={activity.providerImgUrl}
            width={0}
            height={0}
            sizes="100vw"
            className="w-full h-auto aspect-auto"
            quality={80}
          />
        </div>
        {/* MIDDLE PART */}
        <div className="flex flex-col items-center justify-between w-full">
          {isFullScreenVideo && isActivityExpanded && (
            <div className="flex items-center justify-between w-full gap-x-2">
              {/* 1ST MED-LEFT-ITEM */}
              <div
                className={`flex items-center justify-center ${
                  isSingleActivity ? "w-1/4" : "w-1/2"
                }`}
              >
                <div className="flex items-center justify-center">
                  <CircularProgressbar
                    styles={buildStyles({
                      pathColor: `#00F3CD`,
                      trailColor: "#2a423e",
                      textColor: "#00F3CD",
                    })}
                    maxValue={10}
                    strokeWidth={6}
                    value={activity.score}
                    text={`${activity.score / 2}/5`}
                  />
                </div>
              </div>
              {/* 2ND MED-RIGHT-ITEM */}
              <div
                className={`grid gap-y-0.5 w-full ${
                  isSingleActivity ? "grid-cols-2" : ""
                }`}
              >
                <div className={"flex items-center text-white"}>
                  <MdOutlineChat
                    color="white"
                    size={isSingleActivity ? 12 : 14}
                    className="mr-0.5"
                  />
                  <p
                    className={`${
                      isSingleActivity ? "text-[10px]" : "text-xs"
                    }`}
                  >
                    {activity.lang}
                  </p>
                </div>
                <div className={"flex items-center text-white"}>
                  <MdOutlineTimer
                    color="white"
                    size={isSingleActivity ? 12 : 14}
                    className="mr-0.5"
                  />
                  <p
                    className={`${
                      isSingleActivity ? "text-[10px]" : "text-xs"
                    }`}
                  >
                    {activity.lang}
                  </p>
                </div>
                <div
                  className={`flex items-center text-white  ${
                    isSingleActivity ? "col-span-2" : ""
                  }`}
                >
                  <MdOutlinePerson
                    color="white"
                    size={isSingleActivity ? 12 : 14}
                    className="mr-0.5"
                  />
                  <p
                    className={`${
                      isSingleActivity ? "text-[10px]" : "text-xs"
                    }`}
                  >
                    {activity.reviews +
                      " " +
                      translate("ACTIVITY_CARD_OPINIONS")}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* RIGHT PART */}
        <div className="flex flex-col items-center justify-center ml-1">
          <h5
            className={`font-bold text-center capitalize text-primary-color whitespace-nowrap ${
              isSingleActivity ? "text-xl" : "text-3xl"
            }`}
          >
            {price.minimum > 0
              ? `${price.minimum}${price.currency}`
              : translate("FREE")}
          </h5>
          {price.original > price.minimum && (
            <h6 className="relative px-1 text-xs text-gray-400 whitespace-nowrap">
              {`${price.original}${price.currency}`}
              <span className="absolute w-full h-px -translate-x-1/2 -translate-y-1/2 bg-gray-400 left-1/2 top-1/2" />
            </h6>
          )}
          {isActivityExpanded && activity?.freeCancelation && (
            <h6 className="text-white text-tiny whitespace-nowrap">
              {translate("FREE_CANCELLATION")}
            </h6>
          )}
        </div>
      </div>
      <div className={`flex flex-col items-center justify-center py-2`}>
        {isFullScreenVideo && isActivityExpanded && (
          <button
            id={trackingId}
            className={`w-full px-1 py-1 font-bold rounded-md bg-primary-color ${
              isSingleActivity ? "py-1 text-sm" : "py-2"
            }`}
          >
            {translate("SPOT_PROFILE_CHECK_ACTIVITY")}
          </button>
        )}
      </div>
    </a>
  );
};
